import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {
    FormLabel,
    FormControl,
    Image,
    HStack,
    Button,
    Input
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getUrl} from '../../../../utils/cloudinary-utils'
import {getStores} from '../../../../hooks/use-store-search'
import useTagManager from '../../../../hooks/use-tag-manager'
import {useSiteContext} from '../../../../contexts/site'

const SearchField = ({
    searchTerm,
    setSearchTerm,
    setStores,
    setShowPermissionError,
    setHideNoSearchResults
}) => {
    const api = useCommerceApi()
    const intl = useIntl()
    const {isYR} = useSiteContext()
    const tagManager = useTagManager()
    const {getTokenWhenReady} = useAccessToken()
    const {proxy} = api.shopperExperience.clientConfig
    const [isLoading, setIsLoading] = useState(false)

    const handleSubmit = async (e) => {
        e.preventDefault()
        setShowPermissionError(false)
        setHideNoSearchResults(false)
        setStores(null)
        setIsLoading(true)

        tagManager.bopisFindMyStore(searchTerm)

        try {
            const data = await getStores({
                parameters: {
                    c_zip: searchTerm,
                    c_type: 'findbyzip'
                },
                getTokenWhenReady,
                proxy
            })

            if (data?.stores) {
                setStores(data.stores)
                localStorage.setItem('storesSearchTerm', searchTerm)
            } else {
                setStores([])
            }

            setIsLoading(false)
        } catch (error) {
            console.error('Error fetching location:', error)
            setIsLoading(false)
            setStores([])

            return
        }
    }

    return (
        <form onSubmit={handleSubmit}>
            <HStack gap="10px">
                <FormControl variant="floating" id="store-search" isRequired>
                    <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        isReadOnly={isLoading}
                        placeholder=" "
                        width="100%"
                        height="56px"
                        backgroundColor="white"
                    />
                    <FormLabel
                        sx={{
                            '.chakra-form__required-indicator': {
                                display: 'none'
                            }
                        }}
                    >
                        <FormattedMessage
                            id="select_store_modal.zip_or_city_state"
                            defaultMessage="ZIP or City, State"
                        />
                    </FormLabel>
                </FormControl>

                <Button
                    isLoading={isLoading}
                    type="submit"
                    brand={isYR ? 'yr' : 'ks'}
                    aria-label={intl.formatMessage({
                        id: 'select_store_modal.search_stores',
                        defaultMessage: 'Search Stores'
                    })}
                    variant="unstyled"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="48px"
                    height="48px"
                    flex="0 0 48px"
                    borderRadius="50%"
                    background="black.300"
                    _hover={{
                        background: 'black.50'
                    }}
                >
                    <Image
                        alt={intl.formatMessage({
                            id: 'select_store_modal.search_icon',
                            defaultMessage: 'Search Icon'
                        })}
                        width="17px"
                        height="17px"
                        src={getUrl('UIImages/images/search-white.svg')}
                    />
                </Button>
            </HStack>
        </form>
    )
}

SearchField.propTypes = {
    searchTerm: PropTypes.string.isRequired,
    setSearchTerm: PropTypes.func.isRequired,
    setStores: PropTypes.func.isRequired,
    setHideNoSearchResults: PropTypes.func.isRequired,
    setShowPermissionError: PropTypes.func.isRequired
}

export default SearchField
